import { IoTimeOutline, IoAlertCircle } from 'react-icons/io5';
import { MdDone, MdDoneAll } from 'react-icons/md';
import { MessageStatus } from 'src/api/generated';
import Tooltip from '../../tooltips/tooltip';
import type { ComponentVariant } from 'src/modules/shared/types/component.type';
import { Typography } from '@material-tailwind/react';
import {
  PiCheckFatLight,
  PiClockLight,
  PiWarningCircleLight,
} from 'react-icons/pi';

import type {
  MessageStatusIndicatorProps,
  StatusConfig,
} from 'src/modules/shared/types/message-status-indicator.types';

const VARIANT_COLORS: Record<ComponentVariant, string> = {
  primary: 'text-primary-600',
  secondary: 'text-secondary-600',
  danger: 'text-rose-500',
  neutral: 'text-gray-400',
  warning: 'text-warning-600',
  success: 'text-success-600',
  info: 'text-blue-600',
  whatsapp: 'text-whatsapp-600',
  call: 'text-call-600',
  email: 'text-email-600',
};

// TODO: check when implementing i18n
const STATUS_CONFIGS: Record<MessageStatus, StatusConfig | null> = {
  [MessageStatus.PENDING]: {
    icon: <PiClockLight className="h-3 w-3" />,
    text: 'Enviando',
    className: VARIANT_COLORS.neutral,
  },
  [MessageStatus.FAILED]: {
    icon: <PiWarningCircleLight className="h-4 w-4 mt-0.5 stroke-[3px]" />,
    text: 'Fallido',
    className: VARIANT_COLORS.danger,
  },
  [MessageStatus.SENT]: {
    icon: <PiCheckFatLight className="h-3 w-3 stroke-[2px]" />,
    text: 'Enviado',
  },
  [MessageStatus.DELIVERED]: {
    icon: (
      <span className="flex -space-x-1">
        <PiCheckFatLight className="h-3 w-3 stroke-[2px]" />
        <PiCheckFatLight className="h-3 w-3 stroke-[2px]" />
      </span>
    ),
    text: 'Entregado',
  },
  [MessageStatus.READ]: {
    icon: (
      <span className="flex -space-x-1">
        <PiCheckFatLight className="h-3 w-3 stroke-[3px]" />
        <PiCheckFatLight className="h-3 w-3 stroke-[3px]" />
      </span>
    ),
    text: 'Leído',
  },
  [MessageStatus.DELETED]: null,
  [MessageStatus.WARNING]: null,
};

const COMPACT_ICONS = {
  [MessageStatus.PENDING]: <IoTimeOutline className="h-3 w-3" />,
  [MessageStatus.FAILED]: <IoAlertCircle className="h-3.5 w-3.5" />,
  [MessageStatus.SENT]: <MdDone className="h-3.5 w-3.5" />,
  [MessageStatus.DELIVERED]: <MdDoneAll className="h-3.5 w-3.5" />,
  [MessageStatus.READ]: <MdDoneAll className="h-3.5 w-3.5" />,
  [MessageStatus.DELETED]: null,
  [MessageStatus.WARNING]: null,
};

const baseContainerClasses = 'inline-flex items-center gap-1 text-xs';

export const MessageStatusIndicator: React.FC<MessageStatusIndicatorProps> = ({
  status,
  errorMessage,
  variant = 'primary',
  className = '',
  compact = false,
  label,
}) => {
  const config = STATUS_CONFIGS[status];
  if (!config) {
    return compact ? (
      <span aria-hidden="true" className="inline-flex h-4 w-4 shrink-0" />
    ) : null;
  }

  const iconClassName = config.className ?? VARIANT_COLORS[variant];

  if (compact) {
    return (
      <span
        role="img"
        aria-label={label ?? config.text}
        title={label ?? config.text}
        className={`inline-flex h-4 w-4 shrink-0 items-center justify-center ${status === MessageStatus.READ ? 'text-blue-600' : iconClassName} ${className}`}
      >
        {COMPACT_ICONS[status]}
      </span>
    );
  }

  if (status === MessageStatus.FAILED) {
    return (
      <Tooltip content={errorMessage ?? config.text} size="sm" placement="top">
        <span className={`${baseContainerClasses} ${className}`}>
          <span className={iconClassName}>{config.icon}</span>
          <Typography className="text-secondary-800 text-xs">
            {label ?? config.text}
          </Typography>
        </span>
      </Tooltip>
    );
  }

  return (
    <span className={`${baseContainerClasses} ${className}`}>
      <span className={iconClassName}>{config.icon}</span>
      <Typography className="text-secondary-800 text-xs">
        {label ?? config.text}
      </Typography>
    </span>
  );
};
